const PROJECTS = { production: "scervmvp", dev: "scervmvp-dev", testing: "scervmvp-testing" };
const ENVELOPE_KEYS = ["schemaVersion", "firebaseProject", "actorUid", "query", "restaurantId", "observedAtMs", "coverage", "hasMore", "results"];
const RESULT_KEYS = ["kind", "id", "restaurantId", "title", "status", "updatedAtMs"];
export const SEARCH_KINDS = {
	customer: "Customer", restaurant: "Restaurant", order: "Order", reservation: "Reservation",
	device: "Device", drawer: "Drawer command", print: "Print job", party: "Party", table: "Table", support_case: "Support case",
};
const OPERATIONAL_KINDS = ["device", "drawer", "print", "party", "table", "support_case"];
const STATUSES = new Set(["active", "inactive", "new", "open", "closed", "pending", "confirmed", "cancelled", "canceled",
	"waiting", "investigating", "resolved", "paid", "unpaid", "partially_paid", "refunded", "partially_refunded", "failed",
	"queued", "dispatching", "requested", "unknown", "checkedOut", "occupied", "available", "SEATED", "CHECKED_IN",
	"WAITING", "CANCELLED", "COMPLETED", "completed", "enrolled", "revoked", "expired"]);
const id = value => typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
// Support case IDs are never inserted into routes, including broader Firestore document IDs.
// eslint-disable-next-line no-control-regex
const caseId = value => typeof value === "string" && value.length > 0 && value.length <= 500 && value.trim() === value && !/[/\\\x00-\x1f\x7f]/.test(value) && ![".", ".."].includes(value) && !/^__.*__$/.test(value);
const text = (value, max) => typeof value === "string" && value.length > 0 && value.length <= max &&
	// eslint-disable-next-line no-control-regex
	value.trim() === value && !/[\x00-\x1f\x7f]/.test(value);
const time = value => Number.isSafeInteger(value) && value >= 0 && value <= 253402300799999;
const exact = (value, keys) => value !== null && typeof value === "object" && !Array.isArray(value) &&
	Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const fail = () => { throw new Error("Unconfirmed support search response."); };

export function supportSearchEnvironment(auth, functions, environment, selectedKey) {
	const { key, projectId, firebaseConfig } = environment || {};
	return Object.prototype.hasOwnProperty.call(PROJECTS, key) && PROJECTS[key] === projectId && selectedKey === key &&
		firebaseConfig?.projectId === projectId && auth?.app && auth.app === functions?.app &&
		auth.app.options?.projectId === projectId ? `${key}:${projectId}` : null;
}

export function createSupportSearchRequest(firebaseProject, query, restaurantId) {
	if (!Object.values(PROJECTS).includes(firebaseProject) || typeof query !== "string" || query.length > 128 ||
		// eslint-disable-next-line no-control-regex
		query.trim().length < 2 || /[/\\\x00-\x1f\x7f]/.test(query) || typeof restaurantId !== "string" ||
		(restaurantId.trim() !== "" && !id(restaurantId.trim()))) {
		throw new Error("Enter a search term of 2 to 128 characters without slashes, and a valid optional restaurant ID.");
	}
	return { firebaseProject, query: query.trim(), restaurantId: restaurantId.trim() || null };
}

export function readSupportSearchResults(data, { firebaseProject, actorUid, query, restaurantId }) {
	if (!exact(data, ENVELOPE_KEYS) || data.schemaVersion !== 1 || data.firebaseProject !== firebaseProject ||
		data.actorUid !== actorUid || data.query !== query || data.restaurantId !== restaurantId ||
		data.coverage !== "bounded_matches" || !time(data.observedAtMs) || typeof data.hasMore !== "boolean" ||
		!Array.isArray(data.results) || data.results.length > 40) fail();
	const seen = new Set();
	const results = data.results.map(row => {
		if (!exact(row, RESULT_KEYS) || !Object.prototype.hasOwnProperty.call(SEARCH_KINDS, row.kind) ||
			!(row.kind === "support_case" ? caseId(row.id) : id(row.id)) ||
			(row.restaurantId !== null && !id(row.restaurantId)) ||
			(restaurantId !== null && row.restaurantId !== restaurantId) ||
			(!["customer", "support_case"].includes(row.kind) && row.restaurantId === null) ||
			(row.kind === "restaurant" && row.restaurantId !== row.id) ||
			(OPERATIONAL_KINDS.includes(row.kind) && restaurantId === null) ||
			!text(row.title, 180) || !text(row.status, 80) || (row.updatedAtMs !== null && !time(row.updatedAtMs))) fail();
		const key = `${row.kind}:${row.id}`;
		if (seen.has(key)) fail();
		seen.add(key);
		return { kind: row.kind, id: row.id, restaurantId: row.restaurantId, title: row.title,
			status: STATUSES.has(row.status) ? row.status : "unknown", updatedAtMs: row.updatedAtMs };
	});
	return { observedAtMs: data.observedAtMs, hasMore: data.hasMore, results };
}

export function supportSearchLink(row, firebaseProject) {
	if (row.kind === "customer") return { to: `/customers/${encodeURIComponent(row.id)}`, label: "Open customer" };
	if (row.kind === "order") return { to: `/orders/${encodeURIComponent(row.id)}`, label: "Open order" };
	if (row.kind === "support_case") return { to: "/support-cases", label: "Open support cases" };
	if (row.kind === "restaurant") return { to: `/restaurants/${encodeURIComponent(row.id)}`, label: "Open restaurant" };
	const destinations = {
		device: ["devices", "Open device diagnostics"], print: ["printers", "Open printer diagnostics"],
		drawer: ["printers", "Open drawer diagnostics"], party: ["recovery", "Open service diagnostics"],
		table: ["recovery", "Open service diagnostics"], reservation: [null, "Open restaurant reservations"],
	};
	const destination = destinations[row.kind];
	if (!destination || !id(row.restaurantId)) return null;
	const params = new URLSearchParams({ firebaseProject, restaurantId: row.restaurantId,
		tab: row.kind === "reservation" ? "reservations" : "advanced" });
	if (destination[0]) params.set("section", destination[0]);
	return { to: `/restaurants/${encodeURIComponent(row.restaurantId)}?${params}`, label: destination[1] };
}

export function readSupportSearchDestination(search, { firebaseProject, restaurantId }) {
	if (!Object.values(PROJECTS).includes(firebaseProject) || !id(restaurantId) || typeof search !== "string" || search.length > 1024) return null;
	const params = new URLSearchParams(search);
	const keys = params.get("tab") === "reservations" ? ["firebaseProject", "restaurantId", "tab"] : ["firebaseProject", "restaurantId", "tab", "section"];
	if ([...params.keys()].length !== keys.length || !keys.every(key => params.getAll(key).length === 1) ||
		params.get("firebaseProject") !== firebaseProject || params.get("restaurantId") !== restaurantId) return null;
	if (params.get("tab") === "reservations") return { initialTab: "reservations", initialSection: null };
	if (params.get("tab") !== "advanced" || !["devices", "printers", "recovery"].includes(params.get("section"))) return null;
	return { initialTab: "advanced settings", initialSection: params.get("section") };
}
